import { type Ref, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Overline } from '@/components/atoms/Eyebrow';
import Tooltip from '@/components/atoms/Tooltip';
import { parseLocale } from '@/utils/localeFormatters';

import { buildAxisTicks } from '../../helpers/axisTicks';
import { maxTicksFor } from '../../helpers/timelineZoom';
import { drawnTrackStyle } from '../../helpers/trackStyle';
import { buildYearBands, coveredTickKey } from '../../helpers/yearBands';
import type { AxisTick, WeekAxis } from '../../types';
import { TRACK_TIP_DELAY } from '../GroupTrack/TrackTip';

import styles from './AxisHeader.module.scss';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

interface AxisHeaderProps {
    axis: WeekAxis;
    /** Whole weeks drawn after the axis while zoomed; the header labels and grids them too. */
    futureWeeks?: number;
    /** The drawn track's measured width, which sets how many ticks fit; null until measured. */
    trackPx: number | null;
    /** The header spans the track column, so the board measures the track's width on it. */
    ref?: Ref<HTMLDivElement>;
    /** The board's scroller, watched so the label the pinned name column would cut is hidden. */
    scroller?: HTMLElement | null;
}

/**
 * The board's time header: one labelled column per week, month, quarter or year, over the
 * axis and the future weeks drawn after it while zoomed. Week and
 * month labels get a row of years above them whose labels stick to the visible track's left
 * edge, so the year shows wherever a long cycle is scrolled to.
 */
export default function AxisHeader(props: AxisHeaderProps) {
    const { axis, trackPx, ref, scroller = null, futureWeeks = 0 } = props;
    const { startMs } = axis;
    const endMs = axis.endMs + futureWeeks * WEEK_MS;
    const { t, i18n } = useTranslation('fishGroups');
    const monthName = useMemo(() => {
        const format = new Intl.DateTimeFormat(parseLocale(i18n.language), { month: 'short' });
        return (month: number) => format.format(new Date(2000, month, 1));
    }, [i18n.language]);
    const ticks = useMemo(
        () =>
            trackPx === null && futureWeeks === 0
                ? axis.ticks
                : buildAxisTicks(
                      startMs,
                      endMs,
                      trackPx === null ? undefined : maxTicksFor(trackPx),
                      trackPx
                  ),
        [axis.ticks, startMs, endMs, futureWeeks, trackPx]
    );
    const unit = ticks[0]?.unit;
    const years = useMemo(
        () => (unit === 'week' || unit === 'month' ? buildYearBands(startMs, endMs, unit) : []),
        [startMs, endMs, unit]
    );
    // Only the one tick straddling the pinned column changes, so this re-renders the header
    // once per tick scrolled past, not on every scroll event.
    const [covered, setCovered] = useState<string | null>(null);
    // A translation changes the labels' widths, so they are measured again in the new language.
    // biome-ignore lint/correctness/useExhaustiveDependencies: i18n.language re-measures the rendered labels
    useEffect(() => {
        if (!scroller || trackPx === null) return;
        // The straddling label is measured as rendered; a covered one stays laid out, unseen.
        const labelPx = (key: string) =>
            scroller
                .querySelector(`[data-tick="${key}"] .${CSS.escape(styles.label)}`)
                ?.getBoundingClientRect().width ?? 0;
        const update = () =>
            setCovered(coveredTickKey(ticks, trackPx, scroller.scrollLeft, labelPx));
        update();
        scroller.addEventListener('scroll', update, { passive: true });
        return () => scroller.removeEventListener('scroll', update);
    }, [scroller, ticks, trackPx, i18n.language]);

    const label = (tick: AxisTick): string => {
        switch (tick.unit) {
            case 'week':
                return t('board.columns.week', { week: tick.week });
            case 'month':
                return monthName(tick.month);
            case 'quarter':
                return t('board.columns.quarter', { quarter: tick.quarter, year: tick.year });
            case 'year':
                return String(tick.year);
        }
    };

    const title = (tick: AxisTick): string | undefined => {
        switch (tick.unit) {
            case 'week':
                return t('board.columns.weekWithYear', { week: tick.week, year: tick.year });
            case 'month':
                return t('board.columns.monthWithYear', {
                    month: monthName(tick.month),
                    year: tick.year,
                });
            default:
                return undefined;
        }
    };

    return (
        <div ref={ref} className={styles.axis}>
            {years.length > 0 && (
                <div className={styles.years}>
                    {years.map((band) => (
                        <span
                            key={band.key}
                            className={styles.year}
                            style={drawnTrackStyle(band.leftPct, band.widthPct)}>
                            <Overline className={styles.yearLabel}>{band.year}</Overline>
                        </span>
                    ))}
                </div>
            )}
            <div className={styles.ticks}>
                {ticks.map((tick) => (
                    <span
                        key={tick.key}
                        className={styles.tick}
                        data-tick={tick.key}
                        style={drawnTrackStyle(tick.leftPct, tick.widthPct)}>
                        <Tooltip
                            content={title(tick)}
                            layout="fill"
                            delay={TRACK_TIP_DELAY}
                            targetClassName={styles.columnTarget}>
                            {tick.showLabel ? (
                                <Overline
                                    className={classNames(styles.label, {
                                        [styles.labelCovered]: tick.key === covered,
                                    })}>
                                    {label(tick)}
                                </Overline>
                            ) : (
                                <span className={styles.columnTarget} />
                            )}
                        </Tooltip>
                    </span>
                ))}
            </div>
        </div>
    );
}
